import { loadSettings, onSettings, type Settings } from '../meta/settings';

/**
 * Procedural sound effects (Phase 17, DECISIONS D105): every sound is
 * synthesised with WebAudio (oscillators + filtered noise) — no audio files, no
 * licensing questions, nothing to download. The context starts on the first
 * user gesture (browser autoplay rules) and every play is rate-limited per sound
 * so a 200-unit battle never stacks hundreds of voices.
 */
export type SfxId = 'shot' | 'turret' | 'hit' | 'explosion' | 'place' | 'built' | 'trained' | 'horde' | 'alert' | 'click' | 'victory' | 'defeat';

/** Minimum ms between two plays of the same sound. */
const GAP: Record<SfxId, number> = { shot: 70, turret: 90, hit: 60, explosion: 150, place: 80, built: 200, trained: 200, horde: 1500, alert: 1500, click: 40, victory: 2000, defeat: 2000 };

/** Vibration patterns (ms) for the events worth feeling on a phone. */
const BUZZ: Partial<Record<SfxId, number | number[]>> = { horde: [120, 60, 120], alert: 60, explosion: 40, victory: [80, 40, 80, 40, 160], defeat: 300 };

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private settings: Settings = loadSettings();
  private readonly last = new Map<SfxId, number>();
  private readonly off: () => void;
  private readonly unlock = (): void => this.start();
  /** Plays requested (for tests / debug; counted even when muted or without WebAudio). */
  readonly counts: Partial<Record<SfxId, number>> = {};

  constructor() {
    this.off = onSettings((s) => {
      this.settings = s;
      if (this.master) this.master.gain.value = s.sound ? s.volume : 0;
    });
    window.addEventListener('pointerdown', this.unlock, { passive: true });
    window.addEventListener('keydown', this.unlock);
  }

  destroy(): void {
    this.off();
    window.removeEventListener('pointerdown', this.unlock);
    window.removeEventListener('keydown', this.unlock);
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
  }

  get running(): boolean {
    return this.ctx?.state === 'running';
  }

  private start(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => undefined);
      return;
    }
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    try {
      this.ctx = new AC();
    } catch {
      return;
    }
    this.master = this.ctx.createGain();
    this.master.gain.value = this.settings.sound ? this.settings.volume : 0;
    this.master.connect(this.ctx.destination);
    const len = Math.floor(this.ctx.sampleRate * 0.6);
    this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    let seed = 12345;
    for (let i = 0; i < len; i++) {
      seed = (seed * 1103515245 + 12345) >>> 0;
      d[i] = (seed / 0xffffffff) * 2 - 1;
    }
  }

  play(id: SfxId, volume = 1): void {
    const now = performance.now();
    if (now - (this.last.get(id) ?? -Infinity) < GAP[id]) return;
    this.last.set(id, now);
    this.counts[id] = (this.counts[id] ?? 0) + 1;
    const buzz = BUZZ[id];
    if (buzz !== undefined && this.settings.vibration && typeof navigator.vibrate === 'function') {
      try {
        navigator.vibrate(buzz);
      } catch {
        /* not allowed here */
      }
    }
    if (!this.settings.sound || !this.ctx || !this.master || this.ctx.state !== 'running') return;
    const t = this.ctx.currentTime;
    switch (id) {
      case 'shot':
        this.burst(t, 0.06, 2400, 0.35 * volume);
        break;
      case 'turret':
        this.burst(t, 0.09, 1500, 0.45 * volume);
        this.tone(t, 'square', 180, 90, 0.07, 0.12 * volume);
        break;
      case 'hit':
        this.burst(t, 0.04, 900, 0.25 * volume);
        break;
      case 'explosion':
        this.burst(t, 0.5, 400, 0.8 * volume);
        this.tone(t, 'sine', 90, 35, 0.45, 0.5 * volume);
        break;
      case 'place':
        this.tone(t, 'triangle', 300, 220, 0.08, 0.3 * volume);
        this.burst(t + 0.02, 0.05, 1200, 0.15 * volume);
        break;
      case 'built':
        this.tone(t, 'triangle', 392, 392, 0.12, 0.25 * volume);
        this.tone(t + 0.12, 'triangle', 523, 523, 0.18, 0.25 * volume);
        break;
      case 'trained':
        this.tone(t, 'square', 330, 440, 0.12, 0.12 * volume);
        break;
      case 'horde':
        this.tone(t, 'sawtooth', 110, 70, 0.9, 0.22 * volume);
        this.tone(t + 0.15, 'sawtooth', 116, 74, 0.9, 0.18 * volume);
        break;
      case 'alert':
        this.tone(t, 'square', 660, 660, 0.09, 0.15 * volume);
        this.tone(t + 0.14, 'square', 660, 660, 0.09, 0.15 * volume);
        break;
      case 'click':
        this.tone(t, 'sine', 900, 700, 0.03, 0.12 * volume);
        break;
      case 'victory':
        [392, 494, 587, 784].forEach((f, i) => this.tone(t + i * 0.14, 'triangle', f, f, 0.22, 0.25 * volume));
        break;
      case 'defeat':
        [330, 277, 220].forEach((f, i) => this.tone(t + i * 0.22, 'triangle', f, f * 0.97, 0.3, 0.25 * volume));
        break;
    }
  }

  private tone(t: number, type: OscillatorType, from: number, to: number, dur: number, gain: number): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(from, t);
    o.frequency.exponentialRampToValueAtTime(Math.max(20, to), t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.master!);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private burst(t: number, dur: number, cutoff: number, gain: number): void {
    const ctx = this.ctx!;
    const s = ctx.createBufferSource();
    s.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = cutoff;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(f).connect(g).connect(this.master!);
    s.start(t, Math.random() * 0.3);
    s.stop(t + dur + 0.02);
  }
}
